/**
 * Zentrale Währungsliste (ISO 4217, aktive Codes für Zahlungsmittel, Stand 2026) mit den Nachkommastellen der kleinsten
 * Einheit. Statische Tabelle – nicht `Intl.supportedValuesOf('currency')`, denn das hängt von Laufzeit und Browser ab.
 * Ohne Edelmetalle, Sonderziehungsrechte und Testcodes (XAU, XDR, XTS, XXX …). Abgelöste Codes fehlen bewusst
 * (z. B. HRK → EUR 2023, BGN → EUR 2026, ANG → XCG 2025, SLL → SLE, ZWL → ZWG, CUC).
 * Reines TypeScript ohne Server-Abhängigkeiten: Server und Web-App laden es über `@till-erp/platform/currencies`.
 *
 * Einzige Quelle für Währungen in allen Rechenkernen (AD-27): Nachkommastellen, deutscher Geldtext (`formatMoneyDe`)
 * und Umrechnung Fremdwährung → Euro-Cent zum Kurs der Bestellung (`toEuroCents`); nie Gleitkomma.
 * Beträge sind `bigint` in der kleinsten Einheit (EUR: Cent, ISK: ganze Kronen). Artikel- und interne Preise gibt es
 * nur in EUR; Fremdwährungen kommen über Shop-Bestellungen und bleiben in Aufträgen, Belegen, Zahlungen und DATEV
 * mit Betrag und Währung unverändert (Rudolf, 03.10.2026). Unbekannter Code: Aufrufer melden ihn, statt mit 2 Stellen
 * zu raten.
 */
const MINOR_UNITS: Readonly<Record<string, number>> = {
  AED: 2, AFN: 2, ALL: 2, AMD: 2, AOA: 2, ARS: 2, AUD: 2, AWG: 2, AZN: 2,
  BAM: 2, BBD: 2, BDT: 2, BHD: 3, BIF: 0, BMD: 2, BND: 2, BOB: 2, BOV: 2, BRL: 2, BSD: 2, BTN: 2, BWP: 2, BYN: 2, BZD: 2,
  CAD: 2, CDF: 2, CHE: 2, CHF: 2, CHW: 2, CLF: 4, CLP: 0, CNY: 2, COP: 2, COU: 2, CRC: 2, CUP: 2, CVE: 2, CZK: 2,
  DJF: 0, DKK: 2, DOP: 2, DZD: 2,
  EGP: 2, ERN: 2, ETB: 2, EUR: 2,
  FJD: 2, FKP: 2,
  GBP: 2, GEL: 2, GHS: 2, GIP: 2, GMD: 2, GNF: 0, GTQ: 2, GYD: 2,
  HKD: 2, HNL: 2, HTG: 2, HUF: 2,
  IDR: 2, ILS: 2, INR: 2, IQD: 3, IRR: 2, ISK: 0,
  JMD: 2, JOD: 3, JPY: 0,
  KES: 2, KGS: 2, KHR: 2, KMF: 0, KPW: 2, KRW: 0, KWD: 3, KYD: 2, KZT: 2,
  LAK: 2, LBP: 2, LKR: 2, LRD: 2, LSL: 2, LYD: 3,
  MAD: 2, MDL: 2, MGA: 2, MKD: 2, MMK: 2, MNT: 2, MOP: 2, MRU: 2, MUR: 2, MVR: 2, MWK: 2, MXN: 2, MXV: 2, MYR: 2, MZN: 2,
  NAD: 2, NGN: 2, NIO: 2, NOK: 2, NPR: 2, NZD: 2,
  OMR: 3,
  PAB: 2, PEN: 2, PGK: 2, PHP: 2, PKR: 2, PLN: 2, PYG: 0,
  QAR: 2,
  RON: 2, RSD: 2, RUB: 2, RWF: 0,
  SAR: 2, SBD: 2, SCR: 2, SDG: 2, SEK: 2, SGD: 2, SHP: 2, SLE: 2, SOS: 2, SRD: 2, SSP: 2, STN: 2, SVC: 2, SYP: 2, SZL: 2,
  THB: 2, TJS: 2, TMT: 2, TND: 3, TOP: 2, TRY: 2, TTD: 2, TWD: 2, TZS: 2,
  UAH: 2, UGX: 0, USD: 2, USN: 2, UYI: 0, UYU: 2, UYW: 4, UZS: 2,
  VED: 2, VES: 2, VND: 0, VUV: 0,
  WST: 2,
  XAF: 0, XCD: 2, XCG: 2, XOF: 0, XPF: 0,
  YER: 2,
  ZAR: 2, ZMW: 2, ZWG: 2,
};

/** Alle aktiven Codes, alphabetisch. */
export const isoCurrencyCodes: readonly string[] = Object.keys(MINOR_UNITS).sort();

/** Ist `code` ein aktiver ISO-4217-Code (genau so geschrieben, z. B. „EUR“)? */
export function isIsoCurrency(code: unknown): code is string {
  return typeof code === 'string' && Object.hasOwn(MINOR_UNITS, code);
}

/** Eingabe → Code: Leerzeichen weg, Großbuchstaben („ eur “ → „EUR“); `null`, wenn es kein aktiver Code ist. */
export function normalizeCurrencyCode(text: string): string | null {
  const code = text.trim().toUpperCase();
  return isIsoCurrency(code) ? code : null;
}

/** Nachkommastellen der kleinsten Einheit (EUR 2, JPY 0, KWD 3); unbekannter Code → Fehler. */
export function currencyMinorUnits(code: string): number {
  if (!isIsoCurrency(code)) throw new Error(`Unbekannte Währung: ${code}`);
  return MINOR_UNITS[code]!;
}

/** Satz bei ungültigem Währungscode (Formulare, Einstellungen, Schnittstelle). */
export const CURRENCY_INVALID_TEXT = 'Bitte einen gültigen Währungscode nach ISO 4217 eingeben, z. B. EUR, CHF oder USD.';

/** Nachkommastellen der Währung oder `null`, wenn der Code kein aktiver ISO-4217-Code ist. */
export function findCurrencyMinorUnits(code: string): number | null {
  return isIsoCurrency(code) ? MINOR_UNITS[code]! : null;
}

function groupThousands(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/**
 * Betrag in kleinster Einheit → deutscher Text mit den Nachkommastellen der Währung:
 * 180719n, 'EUR' → „1.807,19 €“; 12345n, 'ISK' → „12.345 ISK“; negativ mit echtem Minuszeichen „−“.
 * `short`: ganze Beträge ohne Nachkommastellen (100000n, 'EUR' → „1.000 €“, für Grenzen im Grundtext).
 */
export function formatMoneyDe(minor: bigint, code: string, options: { short?: boolean } = {}): string {
  const minorUnits = currencyMinorUnits(code);
  const negative = minor < 0n;
  const digits = (negative ? -minor : minor).toString().padStart(minorUnits + 1, '0');
  const cut = digits.length - minorUnits;
  const frac = digits.slice(cut);
  const showFrac = minorUnits > 0 && !(options.short && /^0+$/.test(frac));
  const symbol = code === 'EUR' ? '€' : code;
  return `${negative ? '−' : ''}${groupThousands(digits.slice(0, cut))}${showFrac ? `,${frac}` : ''} ${symbol}`;
}

/** Ganzzahlige Division, kaufmännisch gerundet (half-up, symmetrisch um 0); `denominator` > 0. */
export function divRoundHalfUp(numerator: bigint, denominator: bigint): bigint {
  const negative = numerator < 0n;
  const abs = negative ? -numerator : numerator;
  const q = (2n * abs + denominator) / (2n * denominator);
  return negative ? -q : q;
}

/**
 * Fremdwährung → Euro-Cent zum Kurs der Bestellung, kaufmännisch gerundet.
 * `rateMicro` ist der Kurs „Einheiten Fremdwährung je 1 EUR“ mit 6 Nachkommastellen als ganze Zahl
 * (0,94 → 940000n). Die Nachkommastellen der Fremdwährung zählen: 10.000 ISK zu 150 → 6.667 Cent (66,67 €).
 */
export function toEuroCents(minor: bigint, code: string, rateMicro: bigint): bigint {
  const minorUnits = currencyMinorUnits(code);
  if (rateMicro <= 0n) throw new RangeError(`Kurs für ${code} muss größer als 0 sein`);
  return divRoundHalfUp(minor * 100_000_000n, rateMicro * 10n ** BigInt(minorUnits));
}

const DECIMAL = /^(-?)(\d+)(?:\.(\d+))?$/;

/**
 * Dezimalbetrag (Text wie aus Postgres `numeric`, z. B. „38.9000“) in die kleinste Einheit der Währung, kaufmännisch
 * gerundet (half-up, symmetrisch um 0): „38.9000“ EUR → 3890n, „12.4550“ EUR → 1246n, „3890“ JPY → 3890n.
 */
export function decimalToMinor(amount: string, code: string): bigint {
  const m = DECIMAL.exec(amount.trim());
  if (!m) throw new RangeError(`Betrag ${amount}: keine Dezimalzahl.`);
  const minorUnits = currencyMinorUnits(code);
  const frac = m[3] ?? '';
  const kept = BigInt(`${m[2]}${frac.slice(0, minorUnits).padEnd(minorUnits, '0')}`);
  const abs = frac.length > minorUnits && frac.charCodeAt(minorUnits) >= 53 ? kept + 1n : kept; // „5“ oder höher
  return m[1] === '-' && abs !== 0n ? -abs : abs;
}

/**
 * Exakter Dezimalbetrag einer Gegenstelle (Text wie in der Antwort, z. B. „248.04“, „-12.5“, „100“) in die kleinste
 * Einheit der Währung `code` (EUR Cent, JPY ganze Yen, KWD Fils) als Dezimaltext; anders als `decimalToMinor` wird nie
 * gerundet: mehr Nachkommastellen als die Währung hat nur, wenn sie 0 sind. Alles andere (Exponent, Komma, leer, zu
 * groß, unbekannte Währung) → `null`. Nie über eine Gleitkommazahl. „Zu groß“ = mehr als 18 Stellen in der kleinsten
 * Einheit, dieselbe Grenze wie `billing.recordBankPayment` (EBICS/camt Story 13.22, PayPal Story 13.3).
 */
export function decimalToMinorUnits(text: string, code: string): string | null {
  const digits = findCurrencyMinorUnits(code);
  const m = /^(-)?([0-9]{1,16})(?:\.([0-9]+))?$/.exec(text.trim());
  if (digits === null || !m) return null;
  const fraction = m[3] ?? '';
  if (fraction.length > digits && !/^0+$/.test(fraction.slice(digits))) return null;
  const minor = BigInt(`${m[2]!}${fraction.slice(0, digits).padEnd(digits, '0')}`);
  if (minor.toString().length > 18) return null;
  return (m[1] && minor !== 0n ? -minor : minor).toString();
}

/** Kleinste Einheit als Dezimaltext der Währung: 3890n EUR → „38.90“, 3890n JPY → „3890“, 1234n KWD → „1.234“. */
export function minorToDecimal(minor: bigint | string, code: string): string {
  const value = typeof minor === 'bigint' ? minor : BigInt(minor);
  const minorUnits = currencyMinorUnits(code);
  const negative = value < 0n;
  const abs = (negative ? -value : value).toString().padStart(minorUnits + 1, '0');
  const text = minorUnits === 0 ? abs : `${abs.slice(0, -minorUnits)}.${abs.slice(-minorUnits)}`;
  return negative ? `-${text}` : text;
}

/** Nicht-negativer Dezimaltext → ganze Zahl und Nachkommastellen („0.9412“ → 9412n, 4). */
function parseUnsignedDecimal(text: string, what: string): { units: bigint; scale: number } {
  const m = /^(\d+)(?:\.(\d+))?$/.exec(text.trim());
  if (!m) throw new RangeError(`${what} ${text}: keine nicht-negative Dezimalzahl.`);
  const frac = m[2] ?? '';
  return { units: BigInt(`${m[1]}${frac}`), scale: frac.length };
}

function positiveRate(unitsPerEur: string): { units: bigint; scale: number } {
  const rate = parseUnsignedDecimal(unitsPerEur, 'Kurs');
  if (rate.units <= 0n) throw new RangeError(`Kurs ${unitsPerEur} muss größer als 0 sein.`);
  return rate;
}

/**
 * Euro-Einzelpreis → Einzelpreis in der Fremdwährung zum Kurs „Einheiten Fremdwährung je 1 EUR“ (Gegenstück zu
 * `toEuroCents`), kaufmännisch auf 4 Nachkommastellen gerundet (half-up), nie Gleitkomma:
 * „38.9000“ × „0.9412“ = 36,61268 → „36.6127“. Gilt für jede Währung (Einzelpreise haben immer 4 Stellen).
 */
export function eurUnitPriceToCurrency(unitEur: string, unitsPerEur: string): string {
  const price = parseUnsignedDecimal(unitEur, 'Einzelpreis');
  const rate = positiveRate(unitsPerEur);
  const scale = price.scale + rate.scale;
  const product = price.units * rate.units;
  const value = scale >= 4 ? divRoundHalfUp(product, 10n ** BigInt(scale - 4)) : product * 10n ** BigInt(4 - scale);
  const digits = value.toString().padStart(5, '0');
  return `${digits.slice(0, -4)}.${digits.slice(-4)}`;
}

/**
 * Euro-Cent → kleinste Einheit der Fremdwährung zum Kurs „Einheiten Fremdwährung je 1 EUR“, kaufmännisch gerundet
 * (half-up, symmetrisch um 0) auf die Nachkommastellen der Währung: 595n, 'CHF', „0.9412“ → 560n (5,60 CHF);
 * 1000n, 'JPY', „163.5“ → 1635n (1.635 JPY).
 */
export function eurMinorToCurrency(eurCents: bigint, code: string, unitsPerEur: string): bigint {
  const minorUnits = currencyMinorUnits(code);
  const rate = positiveRate(unitsPerEur);
  return divRoundHalfUp(eurCents * rate.units * 10n ** BigInt(minorUnits), 100n * 10n ** BigInt(rate.scale));
}

/**
 * Kleinste Einheit der Fremdwährung → Euro-Cent zum Kurs „Einheiten Fremdwährung je 1 EUR“ als Dezimaltext (Gegenstück
 * zu `eurMinorToCurrency`), kaufmännisch gerundet (half-up), nie Gleitkomma: 10000n USD (100,00 USD) zu „1.0850“ →
 * 9217n (92,17 €). Nur zur Orientierung (Story 10.18, „≈ … €“ im Bestellvorschlag), nicht für Belege.
 */
export function currencyMinorToEurCents(minor: bigint, code: string, unitsPerEur: string): bigint {
  const minorUnits = currencyMinorUnits(code);
  const rate = positiveRate(unitsPerEur);
  return divRoundHalfUp(minor * 100n * 10n ** BigInt(rate.scale), rate.units * 10n ** BigInt(minorUnits));
}
